import MyAvatar from '@/components/my-avatar';
import { useSessionContext } from '@/contexts/session/context';
import type { UseChatMessagesResult } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessageCategory,
  ChatMessagePayloadType,
  ChatMessagePulled,
  type ChatMessage,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import type {
  ChatGroupMember,
  UseChatGroupResult,
} from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { getAvatar } from '@/scripts/utils/genAvatar';
import { isMeMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import type { SetReplyMessage } from '@/scripts/hooks/useReply';
import MessageText from '../messageText';
import MessageWithExpireAnimate from '../messageWithExpireAnimate';
import MessageFile from '../messageFile';
import type { Handlers } from '../messageRenderer';
import { useEditorContext } from '../../../chat-editor';
import ReplyIcon from './images/replyIcon.svg?react';
import styles from './index.module.scss';
// import MessageFile from '../messageFile';

export interface MessageItemWrapperProps {
  message: ChatMessage;
  chat: Chat;
  chatGroup: UseChatGroupResult;
  merge?: boolean;
  highlightLinked?: string;
  handlers?: Handlers;
  setReplyMessage?: SetReplyMessage;
  chatMessages: UseChatMessagesResult;
  children: React.ReactNode | ((displayTime: string) => React.ReactNode);
}

export function MessageItemWrapper({
  message,
  chat,
  handlers,
  chatGroup,
  highlightLinked,
  merge,
  chatMessages,
  children,
}: MessageItemWrapperProps) {
  const { session } = useSessionContext();
  const { editor } = useEditorContext();
  const [user, setUser] = useState<ChatGroupMember>();
  const [replyCount, setReplyCount] = useState(0);

  const isMe = useMemo(() => isMeMessage(message), [message]);

  const animateHighlightLinked = useMemo(() => {
    if (highlightLinked && message.message_id) {
      return highlightLinked === message.message_id;
    }
    return false;
  }, [highlightLinked, message.message_id]);

  const refs = useRefs({
    chat,
    chatGroup,
    session,
    message,
    getLinkMessageCount: chatMessages.getLinkMessageCount,
  });

  const avatarSrc = useMemo(() => {
    if (merge) {
      return '';
    }

    const { chat, session } = refs.current;
    let user_: ChatGroupMember | undefined;

    if (user) {
      user_ = user;
    } else {
      user_ = isMe ? session?.user : (chat.user as any);
      setUser(user_);
    }

    return user_ ? getAvatar(user_.avatar, user_.name) : '';
  }, [isMe, merge, refs, user]);

  const isShowName = useMemo(() => {
    const { chat } = refs.current;
    return chat.category === ChatCategory.GROUP && !isMe;
  }, [isMe, refs]);

  const triggerAllMessagesLinks = useMemo(() => {
    if (!handlers?.allMessages) return [];
    const linkMsgIds = handlers.allMessages.reduce((acc, item) => {
      if (item.link_msg_id && !acc.includes(item.link_msg_id)) {
        acc.push(item.link_msg_id);
      }
      return acc;
    }, [] as string[]);
    return linkMsgIds;
  }, [handlers?.allMessages]);

  useLayoutEffect(() => {
    (async () => {
      if (merge) {
        setUser(undefined);
        return;
      }
      const { chat, chatGroup } = refs.current;
      if (isShowName) {
        const [
          {
            args: [user],
          },
        ] = await chatGroup.getMembersByUserId([
          { id: chat.chat_id, args: [message.owner_id] },
        ]);
        setUser(user);
      }
    })();
  }, [isMe, isShowName, merge, message.owner_id, refs]);

  useLayoutEffect(() => {
    const linkMessageId = `${message.message_id}_${message.owner_id}_${message.payload_type}`;

    triggerAllMessagesLinks.some((str) => {
      if (str === linkMessageId) {
        refs.current.getLinkMessageCount(chat.chat_id, linkMessageId).then((count) => {
          setReplyCount(count);
        });
        return true;
      }
      return false;
    });
  }, [
    chat.chat_id,
    message.message_id,
    message.owner_id,
    message.payload_type,
    refs,
    triggerAllMessagesLinks,
    triggerAllMessagesLinks.length,
  ]);

  return (
    <div
      className={cls(
        styles.messageItem,
        animateHighlightLinked && styles.animateHighlightLinked,
        isMe && styles.messageItemMe,
        merge && styles.messageItemMerge
      )}
    >
      {merge ? (
        <div className={styles.avatarPlaceholder} />
      ) : (
        <MyAvatar size="small" className={styles.avatar} src={avatarSrc} info={user} />
      )}
      <div className={styles.item}>
        <MessageWithExpireAnimate message={message} chat={chat} isMe={isMe}>
          {(displayTime) => (
            <div className={styles.content}>
              {!merge && isShowName && (
                <UserName
                  user={user}
                  onClick={(user) => {
                    editor.insertMention(user);
                  }}
                ></UserName>
              )}
              {typeof children === 'function' ? children(displayTime) : children}
            </div>
          )}
        </MessageWithExpireAnimate>
        {replyCount > 0 && (
          <div className={styles.replyBar}>
            <ReplyIcon />
            <div>{replyCount} Reply</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MessageItem(params: Omit<MessageItemWrapperProps, 'children'>) {
  const { message, chat, setReplyMessage, chatMessages, highlightLinked, handlers } =
    params;
  const { chatGroup, retryMessage } = useChatContext();

  const isMe = useMemo(
    () => message.category === ChatMessageCategory.SEND,
    [message.category]
  );

  return (
    <MessageItemWrapper
      {...params}
      chatGroup={chatGroup}
      highlightLinked={highlightLinked}
    >
      {(displayTime) => (
        <>
          {(() => {
            switch (message.payload_type) {
              case ChatMessagePayloadType.TEXT:
              case ChatMessagePayloadType.ENCRYPTED_TEXT:
                return (
                  <MessageText
                    message={message}
                    chat={chat}
                    chatGroup={chatGroup}
                    handlers={handlers}
                    chatMessages={chatMessages}
                    setReplyMessage={setReplyMessage}
                    isMe={isMe}
                    onClickStatus={() => retryMessage({ chat, message })}
                    displayTime={displayTime ?? ''}
                  />
                );
              case ChatMessagePayloadType.FILE:
              case ChatMessagePayloadType.IMAGE:
                return (
                  <MessageFile
                    message={message}
                    chat={chat}
                    chatGroup={chatGroup}
                    handlers={handlers}
                    chatMessages={chatMessages}
                    setReplyMessage={setReplyMessage}
                    isMe={isMe}
                    onClickStatus={() => retryMessage({ chat, message })}
                    displayTime={displayTime ?? ''}
                  />
                );
              default:
                break;
            }
          })()}
        </>
      )}
    </MessageItemWrapper>
  );
}

export function MessageStatusItem(
  params: Omit<MessageItemWrapperProps, 'children'> & {
    onClickRetry?: () => void;
  }
) {
  const { message } = params;

  return (
    <MessageItemWrapper {...params}>
      {message.pulled === ChatMessagePulled.ERROR ? (
        <div>
          Message load error.
          <Button type="link" size="small" onClick={params.onClickRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <Skeleton.Node active style={{ width: 100, height: 34 }}></Skeleton.Node>
      )}
    </MessageItemWrapper>
  );
}

interface UserNameProps {
  user: ChatGroupMember | undefined;
  onClick?: (user: ChatGroupMember) => void;
}

export function UserName({ user, onClick }: UserNameProps) {
  return (
    <div
      className={styles.userName}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={() => {
        if (user) {
          onClick?.(user);
        }
      }}
    >
      {user?.name}
    </div>
  );
}
